/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useEffect, useMemo, useState } from 'react';

import { Button, Input, Toast, Typography } from '@coze-arch/coze-design';

import styles from '../user-info-panel/index.module.less';

interface OmniChatLinksResponse {
  code?: number;
  data?: {
    links?: Array<{
      omnichat_user_id: string;
      omnichat_email?: string;
    }>;
  };
}

export const OmniChatLinksPanel = () => {
  const [links, setLinks] = useState<
    Array<{ omnichat_user_id: string; omnichat_email?: string }>
  >([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);

  const normalizedInput = useMemo(() => input.trim(), [input]);

  const loadLinks = async () => {
    const response = await fetch('/api/user/omnichat-links');
    const payload = (await response.json()) as OmniChatLinksResponse;
    setLinks(payload.data?.links ?? []);
  };

  useEffect(() => {
    loadLinks().catch(() => {
      Toast.error({ content: 'Failed to load OmniChat links' });
    });
  }, []);

  const handleAdd = async () => {
    if (!normalizedInput) {
      return;
    }
    try {
      setLoading(true);
      const response = await fetch('/api/user/omnichat-links', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ omnichat_user_id: normalizedInput }),
      });
      if (!response.ok) {
        throw new Error('Failed to add OmniChat link');
      }
      setInput('');
      await loadLinks();
      Toast.success({ content: 'OmniChat link added' });
    } catch {
      Toast.error({ content: 'Failed to add OmniChat link' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (omniChatUserId: string) => {
    try {
      setLoading(true);
      const response = await fetch(
        `/api/user/omnichat-links?omnichat_user_id=${encodeURIComponent(omniChatUserId)}`,
        {
          method: 'DELETE',
        },
      );
      if (!response.ok) {
        throw new Error('Failed to delete OmniChat link');
      }
      await loadLinks();
      Toast.success({ content: 'OmniChat link removed' });
    } catch {
      Toast.error({ content: 'Failed to delete OmniChat link' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles['omnichat-links-panel']}>
      <div className={styles['omnichat-links-toolbar']}>
        <Input
          value={input}
          onChange={value => setInput(value)}
          placeholder="Enter OmniChat User UUID"
          maxLength={255}
        />
        <Button
          theme="solid"
          type="primary"
          loading={loading}
          disabled={!normalizedInput}
          onClick={handleAdd}
        >
          Add
        </Button>
      </div>

      <Typography.Text className={styles['omnichat-links-helper']}>
        Manage linked OmniChat accounts for this Coze account.
      </Typography.Text>

      <div className={styles['omnichat-links-list']}>
        {links.length === 0 ? (
          <div className={styles['omnichat-links-empty']}>
            No linked OmniChat users yet.
          </div>
        ) : (
          links.map(link => (
            <div
              key={link.omnichat_user_id}
              className={styles['omnichat-link-row']}
            >
              <Typography.Text ellipsis={{ showTooltip: true }}>
                {link.omnichat_email || link.omnichat_user_id}
              </Typography.Text>
              <Button
                loading={loading}
                onClick={() => handleDelete(link.omnichat_user_id)}
              >
                Delete
              </Button>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
